import { cva } from "class-variance-authority";

const stylesHeaderWrapper = cva(
  "w-full h-[56px] px-[16px] z-[1100] flex items-center justify-between top-0 left-0 right-0"
);

const stylesText = cva("", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

const iconStyles = cva("mr-[16px] flex-shrink-0", {
  variants: {
    mode: {
      light: "text-[#312215]",
      dark: "text-[#D7B09B]"
    }
  }
});

const dropDownStylesText = cva("", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

const dropDownStylesDesText = cva("", {
  variants: {
    mode: {
      light: "text-light-label-40",
      dark: "text-dark-label-40"
    }
  }
});

const stylesHover = cva("flex items-center text-[14px] font-medium cursor-pointer", {
  variants: {
    mode: {
      light: "hover:bg-light-fill-primary hover:text-light-primary",
      dark: "hover:bg-dark-fill-primary hover:text-dark-primary"
    }
  }
});

const stylesBg = cva("", {
  variants: {
    mode: {
      light: "bg-light-background",
      dark: "bg-dark-background"
    }
  }
});

const stylesHeader = cva("", {
  variants: {
    mode: {
      dark: "bg-dark-background",
      light: "bg-light-background"
    }
  }
});

const stylesHeaderSticky = cva("", {
  variants: {
    mode: {
      dark: "bg-dark-background",
      light: "bg-light-background"
    }
  }
});

const activeRouteStyles = cva("", {
  variants: {
    mode: {
      dark: "text-dark-primary",
      light: "text-light-primary"
    }
  }
});

// ─── Mega Menu Styles ────────────────────────────────────────────

/** Mega menu dropdown panel container (显隐由 JS 状态控制，非 CSS group-hover) */
const megaMenuPanel = cva(
  "flex flex-col absolute top-[56px] z-[100] rounded-[16px] overflow-hidden transition-[opacity,transform,visibility] duration-[200ms] ease-in-out origin-top",
  {
    variants: {
      mode: {
        light: "bg-light-background shadow-[0_1px_16px_rgba(0,0,0,0.06)]",
        dark: "bg-dark-fill-secondary backdrop-blur-[15px]"
      }
    }
  }
);

/** Mega menu column container (left column, bg inherited from panel) */
const megaMenuColumn = cva("flex flex-col min-w-0", {
  variants: {
    size: {
      default: "w-[360px] p-[12px]",
      compact: "w-[280px]"
    }
  },
  defaultVariants: { size: "default" }
});

/** Mega menu widget column (right side, slightly different bg) */
const megaMenuWidgetColumn = cva("flex flex-col w-[360px] shrink-0 p-[12px] gap-[8px]", {
  variants: {
    mode: {
      light: "bg-light-fill-primary",
      dark: "bg-dark-hover-fill-secondary backdrop-blur-[15px]"
    }
  }
});

/** Group title inside mega menu (e.g. "Trading", "Explore") */
const megaMenuGroupTitle = cva("text-[14px] leading-[20px] font-medium p-[12px]", {
  variants: {
    mode: {
      light: "text-light-label-40",
      dark: "text-dark-label-40"
    }
  }
});

/** Menu item container (idle state) */
const megaMenuItemContainer = cva(
  "flex items-center gap-[12px] p-[12px] rounded-[12px] cursor-pointer transition-colors duration-150",
  {
    variants: {
      mode: {
        light: "hover:bg-light-fill-secondary",
        dark: "hover:bg-dark-hover-fill-secondary"
      }
    }
  }
);

/** Menu item active/primary state (highlighted bg) */
const megaMenuItemActive = cva(
  "flex items-center gap-[12px] p-[12px] rounded-[12px] cursor-pointer",
  {
    variants: {
      mode: {
        light: "bg-light-fill-secondary",
        dark: "bg-dark-hover-fill-secondary"
      }
    }
  }
);

/** Menu item title */
const megaMenuItemTitle = cva("text-[14px] leading-[20px] font-medium", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

/** Menu item title (primary/active variant – brand color) */
const megaMenuItemTitlePrimary = cva("text-[14px] leading-[20px] font-medium text-light-primary");

/** Menu item description */
const megaMenuItemDesc = cva("text-[12px] leading-[18px]", {
  variants: {
    mode: {
      light: "text-light-label-40",
      dark: "text-dark-label-40"
    }
  }
});

/** Mega menu icon wrapper */
const megaMenuIconWrapper = cva("flex-shrink-0", {
  variants: {
    mode: {
      light: "text-light-label",
      dark: "text-dark-label"
    }
  }
});

/** Search box inside mega menu widget column */
const megaMenuSearchBox = cva(
  "flex items-center gap-[8px] h-[40px] p-[8px] rounded-[8px] border border-solid",
  {
    variants: {
      mode: {
        light: "border-light-line-secondary bg-transparent",
        dark: "border-dark-line-secondary bg-transparent"
      }
    }
  }
);

/** Market list item inside mega menu widget */
const megaMenuMarketItem = cva(
  "flex items-center gap-[2px] p-[12px] rounded-[12px] cursor-pointer transition-colors",
  {
    variants: {
      mode: {
        light: "hover:bg-light-hover-fill-secondary",
        dark: "hover:bg-dark-fill-tertiary"
      }
    }
  }
);

/** Empty state text */
const megaMenuEmptyText = cva("text-[12px] leading-[18px] text-center", {
  variants: {
    mode: {
      light: "text-light-label-40",
      dark: "text-dark-label-40"
    }
  }
});

// ─── Mega Menu Widget Shared Styles ─────────────────────────────

/** Tab bar container with bottom border */
const megaMenuTabBar = cva("flex gap-[16px] w-full border-b border-solid", {
  variants: {
    mode: {
      light: "border-light-line-secondary",
      dark: "border-dark-line-secondary"
    }
  }
});

/** Tab button – base typography (combine with active/inactive variant) */
const megaMenuTabBase = cva(
  "pb-[4px] text-[12px] font-medium cursor-pointer transition-colors border-b-2 border-solid -mb-px bg-transparent leading-[18px]"
);

/** Tab button – active state */
const megaMenuTabActive = cva("", {
  variants: {
    mode: {
      light: "text-light-label border-light-primary",
      dark: "text-dark-label border-dark-primary"
    }
  }
});

/** Tab button – inactive state */
const megaMenuTabInactive = cva("", {
  variants: {
    mode: {
      light: "text-light-label-40 border-transparent hover:text-light-label",
      dark: "text-dark-label-40 border-transparent hover:text-dark-label"
    }
  }
});

/** Hover row container (link items, product cards, trader rows) */
const megaMenuHoverRow = cva(
  "p-[12px] rounded-[12px] cursor-pointer transition-colors duration-150",
  {
    variants: {
      mode: {
        light: "hover:bg-light-hover-fill-secondary",
        dark: "hover:bg-dark-fill-tertiary"
      }
    }
  }
);

/** Link/card title with group-hover → primary color transition */
const megaMenuLinkTitle = cva("transition-colors", {
  variants: {
    mode: {
      light: "text-light-label group-hover:text-light-primary",
      dark: "text-dark-label group-hover:text-dark-primary"
    }
  }
});

/** Search input text + placeholder colors */
const megaMenuInputText = cva("", {
  variants: {
    mode: {
      light: "text-light-label placeholder:text-light-label-40",
      dark: "text-dark-label placeholder:text-dark-label-40"
    }
  }
});

/** Search box active/focused border override */
const megaMenuSearchBoxActive = cva("", {
  variants: {
    mode: {
      light: "!border-light-label",
      dark: "!border-dark-label"
    }
  }
});

/** Dismiss/clear button text (secondary → primary on hover) */
const megaMenuDismissButton = cva("", {
  variants: {
    mode: {
      light: "text-light-label-40 hover:text-light-label",
      dark: "text-dark-label-40 hover:text-dark-label"
    }
  }
});

/** Tertiary label text (60% opacity) */
const megaMenuLabel60 = cva("", {
  variants: {
    mode: {
      light: "text-light-label-60",
      dark: "text-dark-label-60"
    }
  }
});

/** Skeleton / placeholder background */
const megaMenuSkeletonBg = cva("", {
  variants: {
    mode: {
      light: "bg-light-hover-fill-secondary",
      dark: "bg-dark-fill-tertiary"
    }
  }
});

// ─── Asset Popper Styles ────────────────────────────────────────

/** Asset / Reward card hover background */
const assetPopperCardHover = cva("", {
  variants: {
    mode: {
      light: "hover:bg-light-fill-primary",
      dark: "hover:bg-dark-hover-fill-secondary"
    }
  }
});

/** Label-40 → label on named group-hover (e.g. group-hover/assetcard) */
const assetPopperGroupHoverLabel = cva("", {
  variants: {
    mode: {
      light: "text-light-label-40 group-hover/assetcard:text-light-label",
      dark: "text-dark-label-40 group-hover/assetcard:text-dark-label"
    }
  }
});

/** Quick-action icon/label → primary on named group-hover (group/quickaction) */
const assetPopperQuickActionHover = cva("", {
  variants: {
    mode: {
      light: "group-hover/quickaction:text-light-primary",
      dark: "group-hover/quickaction:text-dark-primary"
    }
  }
});

/** Reward countdown badge background */
const assetPopperBadgeBg = cva("", {
  variants: {
    mode: {
      light: "bg-light-hover-fill-secondary",
      dark: "bg-dark-fill-tertiary"
    }
  }
});

// ─── Overflow Menu Styles ───────────────────────────────────────

/** Sub-item text → primary on named group-hover (group/sub) */
const overflowSubHoverText = cva("", {
  variants: {
    mode: {
      light: "group-hover/sub:text-light-primary",
      dark: "group-hover/sub:text-dark-primary"
    }
  }
});

// ─── Popper Shared Styles ───────────────────────────────────────

/** Popper panel background (consistent with megaMenuPanel) */
const popperPanelBg = cva("rounded-[16px] shadow-[0_2px_20px_rgba(0,0,0,.12)]", {
  variants: {
    mode: {
      light: "bg-light-background",
      dark: "bg-dark-fill-secondary backdrop-blur-[15px]"
    }
  }
});

/** Popper menu item hover (bg aligned with megaMenu, text → primary) */
const popperItemHover = cva("cursor-pointer transition-colors duration-150", {
  variants: {
    mode: {
      light: "hover:bg-light-fill-secondary hover:text-light-primary",
      dark: "hover:bg-dark-hover-fill-secondary hover:text-dark-primary"
    }
  }
});

/** Trigger icon/text hover to primary color */
const popperTriggerHover = cva("", {
  variants: {
    mode: {
      light: "hover:text-light-primary",
      dark: "hover:text-dark-primary"
    }
  }
});

/** Divider line */
const popperDivider = cva("", {
  variants: {
    mode: {
      light: "bg-light-line-primary",
      dark: "bg-dark-line-primary"
    }
  }
});

/** Ghost button border (secondary → primary on hover) */
const popperButtonBorder = cva("", {
  variants: {
    mode: {
      light: "border-light-line-secondary hover:border-light-primary",
      dark: "border-dark-line-secondary hover:border-dark-primary"
    }
  }
});

export {
  stylesHeaderWrapper,
  stylesText,
  stylesHover,
  stylesBg,
  dropDownStylesText,
  stylesHeader,
  stylesHeaderSticky,
  dropDownStylesDesText,
  iconStyles,
  activeRouteStyles,
  // Mega menu styles
  megaMenuPanel,
  megaMenuColumn,
  megaMenuWidgetColumn,
  megaMenuGroupTitle,
  megaMenuItemContainer,
  megaMenuItemActive,
  megaMenuItemTitle,
  megaMenuItemTitlePrimary,
  megaMenuItemDesc,
  megaMenuIconWrapper,
  megaMenuSearchBox,
  megaMenuMarketItem,
  megaMenuEmptyText,
  // Mega menu widget shared styles
  megaMenuTabBar,
  megaMenuTabBase,
  megaMenuTabActive,
  megaMenuTabInactive,
  megaMenuHoverRow,
  megaMenuLinkTitle,
  megaMenuInputText,
  megaMenuSearchBoxActive,
  megaMenuDismissButton,
  megaMenuLabel60,
  megaMenuSkeletonBg,
  // Overflow menu styles
  overflowSubHoverText,
  // Asset popper styles
  assetPopperCardHover,
  assetPopperGroupHoverLabel,
  assetPopperQuickActionHover,
  assetPopperBadgeBg,
  // Popper shared styles
  popperPanelBg,
  popperItemHover,
  popperTriggerHover,
  popperDivider,
  popperButtonBorder
};
